import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SimpleGrid,
  Select,
  Card,
  Text,
  Group,
  Stack,
  Badge,
  Skeleton,
  Box,
} from "@mantine/core";
import { DonutChart, BarChart } from "@mantine/charts";
import { useQuery } from "@tanstack/react-query";
import {
  IconUsers,
  IconCar,
  IconRoute,
  IconCash,
} from "@tabler/icons-react";
import { dashboardApi } from "../api/dashboard";
import type { DashboardPeriod } from "../types/admin";
import { PageHeader } from "../components/common/PageHeader";
import { StatCard } from "../components/common/StatCard";
import { QueryError } from "../components/common/QueryError";
import { DataTable, type Column } from "../components/common/DataTable";
import { StatusBadge } from "../components/common/StatusBadge";
import { formatMoney, formatNumber } from "../lib/format";
import type { RecentTrip } from "../types/admin";

const PERIODS: { value: DashboardPeriod; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
  { value: "last_3_months", label: "Last 3 months" },
  { value: "last_year", label: "Last year" },
  { value: "all_time", label: "All time" },
];

const PERIOD_KEY = "safiri.admin.dashboardPeriod";

export function DashboardPage() {
  const navigate = useNavigate();
  // Persist the selected range so it survives navigating away and back.
  const [period, setPeriodState] = useState<DashboardPeriod>(
    () => (localStorage.getItem(PERIOD_KEY) as DashboardPeriod) ?? "this_month",
  );
  const setPeriod = (p: DashboardPeriod) => {
    localStorage.setItem(PERIOD_KEY, p);
    setPeriodState(p);
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard", period],
    queryFn: () => dashboardApi.get(period),
  });

  const recentColumns: Column<RecentTrip>[] = [
    { key: "user", header: "Rider", render: (t) => t.userName || "—" },
    { key: "driver", header: "Driver", render: (t) => t.driverName || "—" },
    {
      key: "fare",
      header: "Fare",
      align: "right",
      render: (t) => formatMoney(t.fare),
    },
    {
      key: "status",
      header: "Status",
      render: (t) => <StatusBadge status={t.status} />,
    },
  ];

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Operational overview"
        actions={
          <Select
            data={PERIODS}
            value={period}
            onChange={(v) => setPeriod((v as DashboardPeriod) ?? "this_month")}
            allowDeselect={false}
            w={170}
          />
        }
      />

      {error && (
        <Box mb="lg">
          <QueryError error={error} onRetry={refetch} />
        </Box>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} mb="lg">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} h={130} radius="md" />
          ))
        ) : (
          <>
            <StatCard
              label="Riders"
              value={formatNumber(data?.users.total)}
              icon={IconUsers}
              trend={data?.users.trend}
              sub={`${formatNumber(data?.users.active)} active`}
            />
            <StatCard
              label="Drivers"
              value={formatNumber(data?.drivers.total)}
              icon={IconCar}
              trend={data?.drivers.trend}
              sub={`${formatNumber(data?.drivers.pending)} pending approval`}
            />
            <StatCard
              label="Trips"
              value={formatNumber(data?.trips.total)}
              icon={IconRoute}
              trend={data?.trips.trend}
              sub={`${formatNumber(data?.trips.completed)} completed`}
            />
            <StatCard
              label="Revenue"
              value={formatMoney(data?.revenue.total)}
              icon={IconCash}
              trend={data?.revenue.trend}
              sub={`${formatMoney(data?.revenue.platform)} platform`}
            />
          </>
        )}
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} mb="lg">
        <Card withBorder radius="md" padding="lg">
          <Text fw={600} mb="md">
            Trip Status
          </Text>
          {isLoading || !data ? (
            <Skeleton h={180} />
          ) : (
            <Group justify="center">
              <DonutChart
                size={180}
                thickness={28}
                withLabelsLine
                data={[
                  {
                    name: "Completed",
                    value: data.trips.completed,
                    color: "var(--success-solid)",
                  },
                  {
                    name: "In Progress",
                    // active sub-statuses (accepted/driverArrived/inProgress/…),
                    // from the backend, with a derived fallback.
                    value:
                      data.trips.inProgress ??
                      Math.max(
                        data.trips.total -
                          data.trips.completed -
                          data.trips.cancelled,
                        0,
                      ),
                    color: "var(--info-solid)",
                  },
                  {
                    name: "Cancelled",
                    value: data.trips.cancelled,
                    color: "var(--error-solid)",
                  },
                ]}
              />
            </Group>
          )}
        </Card>

        <Card withBorder radius="md" padding="lg">
          <Text fw={600} mb="md">
            Revenue split
          </Text>
          {isLoading || !data ? (
            <Skeleton h={180} />
          ) : (
            <BarChart
              h={180}
              data={[
                {
                  label: "Revenue",
                  Platform: data.revenue.platform,
                  Driver: data.revenue.driver,
                },
              ]}
              dataKey="label"
              series={[
                { name: "Platform", color: "var(--viz-cat-1)" },
                { name: "Driver", color: "var(--accent-driver-solid)" },
              ]}
              valueFormatter={(v) => formatMoney(v)}
              tickLine="y"
              yAxisProps={{ width: 80 }}
            />
          )}
        </Card>
      </SimpleGrid>

      {/* Attention cards */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        <MiniStat
          label="SOS alerts"
          total={data?.sosAlerts.total}
          pending={data?.sosAlerts.active}
          attentionLabel="active"
          attentionColor="red"
          loading={isLoading}
          onClick={() => navigate("/sos")}
        />
        <MiniStat
          label="Support queries"
          total={data?.queries.total}
          pending={data?.queries.pending}
          loading={isLoading}
          onClick={() => navigate("/queries")}
        />
        <MiniStat
          label="Lost & found"
          total={data?.lostItems.total}
          pending={data?.lostItems.pending}
          loading={isLoading}
          onClick={() => navigate("/lost-items")}
        />
      </SimpleGrid>

      <Card withBorder radius="md" padding="lg">
        <Text fw={600} mb="md">
          Recent trips
        </Text>
        <DataTable
          columns={recentColumns}
          rows={data?.recentTrips ?? []}
          rowKey={(t) => t.tripId}
          loading={isLoading}
          emptyLabel="No recent trips"
          onRowClick={(t) => navigate(`/trips/${t.tripId}`)}
        />
      </Card>
    </>
  );
}

function MiniStat({
  label,
  total,
  pending,
  loading,
  onClick,
  attentionLabel = "pending",
  attentionColor = "yellow",
}: {
  label: string;
  total?: number;
  pending?: number;
  loading?: boolean;
  onClick: () => void;
  attentionLabel?: string;
  attentionColor?: string;
}) {
  if (loading) return <Skeleton h={92} radius="md" />;
  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      <Stack gap={6}>
        <Text size="sm" c="dimmed" fw={600}>
          {label}
        </Text>
        <Group justify="space-between" align="center">
          <Text fw={700} fz={24}>
            {formatNumber(total)}
          </Text>
          {(pending ?? 0) > 0 && (
            <Badge color={attentionColor} variant="light">
              {formatNumber(pending)} {attentionLabel}
            </Badge>
          )}
        </Group>
      </Stack>
    </Card>
  );
}
